import WebSocketConnectionStatus from '../../models/enums/websocket-connection-status';
import HttpError from '@common/interfaces/api/http-error';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import PaginationWrapper from '../../models/misc/api/pagination-wrapper';
import RestSet, { defaultRestSet } from '@common/interfaces/api/rest-set';
import Mission from '../../models/missions/mission';
import MissionList from '../../models/missions/mission-list';
import { DeploymentDetails } from '../../models/organization/deployment';
import ActionsList from '../../models/robots/actions-list';
import DailyCleanedArea from '../../models/robots/daily-cleaned-area';
import Robot from '../../models/robots/robot';
import RobotContact from '../../models/robots/robot-contact';
import RobotInformation from '../../models/robots/robot-information';
import TorizonStatus from '../../models/robots/torizon-status';
import TorizonReadiness from '../../models/robots/torizon-readiness';
import ValidationResult from '@common/interfaces/api/validation-result';
import LocalRobotDataState from 'models/robots/local-robot-data-state';
import RobotPosition from '@common/interfaces/cleaning-map/robot-position';
import {
  RobotMissionState,
  RobotSchedulingState,
} from '@common/interfaces/robot/status/robot-state';
import MapInfo from '@common/interfaces/cleaning-map/map-info';

export interface RobotRestSet extends RestSet {
  getRobotInformationStatus: HttpFetchStatus;
  getRobotInformationError: HttpError | null;
  getWireguardConfigStatus: HttpFetchStatus;
  getWireguardConfigError: HttpError | null;
  getOrgId: string | null;
  getDailyCleanedAreaStatus: HttpFetchStatus;
  getDailyCleanedAreaError: HttpError | null;
  getDeploymentsStatus: HttpFetchStatus;
  getDeploymentsError: HttpError | null;
  patchDeploymentsStatus: HttpFetchStatus;
  patchDeploymentsError: HttpError | null;
  patchDeploymentDatesStatus: HttpFetchStatus;
  patchDeploymentDatesError: HttpError | null;
  reassignMissionsStatus: HttpFetchStatus;
  reassignMissionsError: HttpError | null;
  getContactsStatus: HttpFetchStatus;
  getContactsError: HttpError | null;
  getLocalRobotDataStatus: HttpFetchStatus;
  getLocalRobotDataError: HttpError | null;
  patchAuthenticationSettingsStatus: HttpFetchStatus;
  patchAuthenticationSettingsError: HttpError | ValidationResult[] | null;
}

export interface MissionRestSet extends RestSet {
  getMissionInformationStatus: HttpFetchStatus;
  getMissionInformationError: HttpError | null;
}

interface RobotsState {
  robots: {
    pagination: PaginationWrapper<Robot> | null;
    currentRobot: RobotInformation | null;
    currentRobotData: Partial<{
      actionsList: ActionsList | null;
      dailyCleanedArea: DailyCleanedArea | null;
      wireguardConfig: string | null;
      deployments: DeploymentDetails[] | null;
      statusTimestamp: string | null;
      contacts: RobotContact[] | null;
      localDataState: LocalRobotDataState;
      position: RobotPosition | null;
      coverageMap: MapInfo | null;
      missionStatus: RobotMissionState | null;
      schedulingStatus: RobotSchedulingState | null;
    }>;
    rest: RobotRestSet;
    selectedId: string | null;
    robotHubState: WebSocketConnectionStatus;
  };

  missions: {
    pagination: PaginationWrapper<MissionList> | null;
    currentMission: Mission | null;
    rest: MissionRestSet;
    selectedId: string | null;
  };

  tags: {
    data: string[] | null;
    rest: RestSet;
  };
  errors: {
    data: Error[];
    rest: RestSet;
  };
  torizon: {
    status: TorizonStatus | null;
    readiness: TorizonReadiness | null;
    readinessPending: boolean;
    rest: {
      getStatus: HttpFetchStatus;
      getError: HttpError | null;
      updateStatus: HttpFetchStatus;
      updateError: HttpError | null;
      cancelStatus: HttpFetchStatus;
      cancelError: HttpError | null;
    };
  };
}

export const defaultTorizonSet = {
  status: null,
  readiness: null,
  readinessPending: false,
  rest: {
    getStatus: HttpFetchStatus.Idle,
    getError: null,
    updateStatus: HttpFetchStatus.Idle,
    updateError: null,
    cancelStatus: HttpFetchStatus.Idle,
    cancelError: null,
  },
};

export const defaultRobotRestSet = {
  ...defaultRestSet,
  getRobotInformationStatus: HttpFetchStatus.Idle,
  getRobotInformationError: null,
  getOrgId: null,
  getDailyCleanedAreaStatus: HttpFetchStatus.Idle,
  getDailyCleanedAreaError: null,
  getDeploymentsStatus: HttpFetchStatus.Idle,
  getDeploymentsError: null,
  patchDeploymentsStatus: HttpFetchStatus.Idle,
  patchDeploymentsError: null,
  patchDeploymentDatesStatus: HttpFetchStatus.Idle,
  patchDeploymentDatesError: null,
  reassignMissionsStatus: HttpFetchStatus.Idle,
  reassignMissionsError: null,
  getContactsStatus: HttpFetchStatus.Idle,
  getContactsError: null,
  getLocalRobotDataStatus: HttpFetchStatus.Idle,
  getLocalRobotDataError: null,
  getWireguardConfigStatus: HttpFetchStatus.Idle,
  getWireguardConfigError: null,
  patchAuthenticationSettingsStatus: HttpFetchStatus.Idle,
  patchAuthenticationSettingsError: null,
};

export const getInitialRobotsState = (): RobotsState => {
  return {
    robots: {
      pagination: null,
      currentRobot: null,
      currentRobotData: {},
      selectedId: null,
      robotHubState: WebSocketConnectionStatus.DISCONNECTED,
      rest: defaultRobotRestSet,
    },
    missions: {
      pagination: null,
      currentMission: null,
      selectedId: null,
      rest: {
        ...defaultRestSet,
        getMissionInformationStatus: HttpFetchStatus.Idle,
        getMissionInformationError: null,
      },
    },
    tags: {
      data: null,
      rest: defaultRestSet,
    },
    errors: {
      data: [],
      rest: defaultRestSet,
    },
    torizon: defaultTorizonSet,
  };
};
export type { RobotsState };
